import { useState } from 'react'
import { Button, Input, Space } from 'antd'
import { ReloadOutlined, PlusOutlined, SearchOutlined } from '@ant-design/icons'
import { AppLayout } from '../../../components/AppLayout'
import { Loading } from '../../../components/Loading'
import { EmptyState } from '../../../components/EmptyState'
import { useSurveys } from '../hooks/useSurveys'
import { SurveyConfigTable } from '../components/SurveyConfigTable'
import { CreateSurveyModal } from '../components/CreateSurveyModal'
import { EditSurveyModal } from '../components/EditSurveyModal'
import { SurveyQuestionsDrawer } from '../components/SurveyQuestionsDrawer'
import { SurveyResponsesDrawer } from '../components/SurveyResponsesDrawer'
import type { SurveyConfig } from '../types/survey.types'

export function SurveysPage() {
  const [searchTerm, setSearchTerm] = useState('')
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedSurvey, setSelectedSurvey] = useState<SurveyConfig | null>(null)
  const [questionsDrawerOpen, setQuestionsDrawerOpen] = useState(false)
  const [questionsSurveyId, setQuestionsSurveyId] = useState<string | null>(null)
  const [questionsSurveyTitle, setQuestionsSurveyTitle] = useState<string | null>(null)
  const [responsesDrawerOpen, setResponsesDrawerOpen] = useState(false)
  const [responsesSurveyId, setResponsesSurveyId] = useState<string | null>(null)
  const [responsesSurveyTitle, setResponsesSurveyTitle] = useState<string | null>(null)

  const { surveys, loading, error, refresh } = useSurveys()

  const filteredSurveys = searchTerm.trim()
    ? surveys.filter(s => s.title.toLowerCase().includes(searchTerm.toLowerCase()))
    : surveys

  const handleEdit = (survey: SurveyConfig) => {
    setSelectedSurvey(survey)
    setEditModalOpen(true)
  }

  const handleEditClose = () => {
    setEditModalOpen(false)
    setSelectedSurvey(null)
  }

  const handleManageQuestions = (survey: SurveyConfig) => {
    setQuestionsSurveyId(survey.id)
    setQuestionsSurveyTitle(survey.title)
    setQuestionsDrawerOpen(true)
  }

  const handleQuestionsDrawerClose = () => {
    setQuestionsDrawerOpen(false)
    setQuestionsSurveyId(null)
    setQuestionsSurveyTitle(null)
  }

  const handleViewResponses = (survey: SurveyConfig) => {
    setResponsesSurveyId(survey.id)
    setResponsesSurveyTitle(survey.title)
    setResponsesDrawerOpen(true)
  }

  const handleResponsesDrawerClose = () => {
    setResponsesDrawerOpen(false)
    setResponsesSurveyId(null)
    setResponsesSurveyTitle(null)
  }

  return (
    <AppLayout>
      <div style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 112px)' }}>
        <div
          style={{
            marginBottom: 16,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '16px',
            flexShrink: 0
          }}
        >
          <Space size="middle" wrap>
            <Input
              placeholder="Search by title..."
              prefix={<SearchOutlined />}
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              style={{ width: 350 }}
              allowClear
            />
            <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateModalOpen(true)}>
              New Survey
            </Button>
          </Space>
        </div>
        <div style={{ flex: 1, minHeight: 0 }}>
          {loading && surveys.length === 0 ? (
            <Loading />
          ) : error ? (
            <EmptyState description={error.message} />
          ) : filteredSurveys.length === 0 && searchTerm ? (
            <EmptyState description={`No surveys found matching "${searchTerm}"`} />
          ) : filteredSurveys.length === 0 ? (
            <EmptyState description="No surveys configured yet" />
          ) : (
            <SurveyConfigTable
              surveys={filteredSurveys}
              loading={loading}
              onEdit={handleEdit}
              onManageQuestions={handleManageQuestions}
              onViewResponses={handleViewResponses}
              onRefresh={refresh}
            />
          )}
        </div>
      </div>

      <CreateSurveyModal open={createModalOpen} onClose={() => setCreateModalOpen(false)} onSuccess={refresh} />
      <EditSurveyModal open={editModalOpen} survey={selectedSurvey} onClose={handleEditClose} onSuccess={refresh} />
      <SurveyQuestionsDrawer
        open={questionsDrawerOpen}
        surveyId={questionsSurveyId}
        surveyTitle={questionsSurveyTitle}
        onClose={handleQuestionsDrawerClose}
      />
      <SurveyResponsesDrawer
        open={responsesDrawerOpen}
        surveyId={responsesSurveyId}
        surveyTitle={responsesSurveyTitle}
        onClose={handleResponsesDrawerClose}
      />
    </AppLayout>
  )
}
